import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, MessageCircle, Send } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { CONTENT, telHref, whatsappHref, SERVICES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", service: "", message: "" };

const inputCls =
  "w-full border border-white/15 bg-[#050505] px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-[#0055FF]";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Enquiry sent. We will get back to you shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Could not send your enquiry. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#0A0A0A] px-6 py-24 md:px-12 md:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHead
          id="contact"
          overline="Chapter 08 — Get In Touch"
          title="Request a Free Quote"
          desc="Tell us about your project — structure type, site and requirements — and we will respond with a practical way forward."
        />

        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-8">
              <a data-testid="contact-phone-link" href={telHref()} className="group flex items-start gap-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-white/15 transition-colors group-hover:border-[#0055FF]">
                  <Phone size={20} className="text-[#3377FF]" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-white/50">Call Us</span>
                  <span className="mt-1 block font-display text-xl font-semibold transition-colors group-hover:text-[#3377FF]">
                    {CONTENT.phone}
                  </span>
                </span>
              </a>
              <a
                data-testid="contact-whatsapp-link"
                href={whatsappHref()}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-5"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-white/15 transition-colors group-hover:border-[#0055FF]">
                  <MessageCircle size={20} className="text-[#3377FF]" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-white/50">WhatsApp</span>
                  <span className="mt-1 block font-display text-xl font-semibold transition-colors group-hover:text-[#3377FF]">
                    Chat with us instantly
                  </span>
                </span>
              </a>
              <div className="flex items-start gap-5" data-testid="contact-address">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-white/15">
                  <MapPin size={20} className="text-[#3377FF]" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.25em] text-white/50">Location</span>
                  <span className="mt-1 block font-display text-xl font-semibold">{CONTENT.address}</span>
                </span>
              </div>
              <p className="border-l-2 border-[#0055FF] pl-5 text-sm leading-relaxed text-[#A3A3A3]">
                Serving residential, commercial and industrial projects across Hyderabad and surrounding areas of
                Telangana.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            <form
              data-testid="quote-form"
              onSubmit={submit}
              className="grid grid-cols-1 gap-5 border border-white/10 bg-[#050505] p-8 sm:grid-cols-2 md:p-10"
            >
              <input
                data-testid="quote-form-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your Name *"
                className={inputCls}
              />
              <input
                data-testid="quote-form-phone-input"
                required
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone Number *"
                className={inputCls}
              />
              <input
                data-testid="quote-form-email-input"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email (optional)"
                className={inputCls}
              />
              <select
                data-testid="quote-form-service-select"
                value={form.service}
                onChange={set("service")}
                className={`${inputCls} appearance-none`}
              >
                <option value="">Service Required</option>
                {SERVICES.map((s) => (
                  <option key={s.title} value={s.title}>
                    {s.title}
                  </option>
                ))}
              </select>
              <textarea
                data-testid="quote-form-message-input"
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us about your project — site, dimensions, requirements..."
                rows={4}
                className={`${inputCls} sm:col-span-2 resize-none`}
              />
              <button
                data-testid="quote-form-submit-button"
                type="submit"
                disabled={sending}
                className="flex items-center justify-center gap-3 bg-[#0055FF] px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-white transition-all hover:bg-[#3377FF] disabled:opacity-60 sm:col-span-2 sm:w-auto"
              >
                {sending ? "Sending..." : "Send Enquiry"} <Send size={15} />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
